import React from "react"
import { useSelector, useDispatch } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import { useFormik } from "formik"
import * as Yup from "yup"
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Avatar,
  IconButton,
} from "@mui/material"
import HomeIcon from "@mui/icons-material/Home"
import FavoriteIcon from "@mui/icons-material/Favorite"
import { userForgetPassword } from "../../store/actions"
import profile from "../../assets/images/profile-img.png"
import logo from "../../assets/images/logo.png"

// Validation schema
const validationSchema = Yup.object({
  email: Yup.string()
    .email("Please enter a valid email")
    .required("Email is required"),
})

const MuiForgetPassword = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { forgetError, forgetSuccessMsg } = useSelector((state) => ({
    forgetError: state.ForgetPassword.forgetError,
    forgetSuccessMsg: state.ForgetPassword.forgetSuccessMsg,
  }))

  // Set page title
  document.title = "Forget Password | FlexFlow - React Admin & Dashboard Template"

  // Formik form handling
  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema,
    onSubmit: (values) => {
      dispatch(userForgetPassword(values, navigate))
    },
  })

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "grey.100" }}>
      {/* Home button */}
      <IconButton
        component={Link}
        to="/"
        sx={{
          position: "fixed",
          top: 16,
          right: 16,
          display: { xs: "none", sm: "flex" },
          color: "text.secondary",
        }}
      >
        <HomeIcon fontSize="large" />
      </IconButton>

      <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 8 } }}>
        <Paper elevation={3} sx={{ overflow: "hidden" }}>
          {/* Header section */}
          <Box
            sx={{
              bgcolor: "primary.light",
              py: 4,
              px: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                variant="h5"
                color="primary.main"
                fontWeight={600}
                gutterBottom
              >
                Reset Password
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter your email to reset your password.
              </Typography>
            </Box>
            <Box
              component="img"
              src={profile}
              alt="Profile"
              sx={{
                maxWidth: 120,
                height: "auto",
              }}
            />
          </Box>

          {/* Logo */}
          <Box sx={{ px: 3, mt: -3 }}>
            <Avatar
              component={Link}
              to="/"
              sx={{
                width: 72,
                height: 72,
                bgcolor: "background.paper",
                boxShadow: 1,
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Logo"
                sx={{ width: 40, height: 40 }}
              />
            </Avatar>
          </Box>

          {/* Form section */}
          <Box sx={{ p: 3 }}>
            {forgetError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {forgetError}
              </Alert>
            )}

            {forgetSuccessMsg && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {forgetSuccessMsg}
              </Alert>
            )}

            <form onSubmit={formik.handleSubmit}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email"
                type="email"
                placeholder="Enter email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                sx={{ mb: 3 }}
              />

              <Box sx={{ textAlign: "right" }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{ minWidth: 120 }}
                >
                  Reset
                </Button>
              </Box>
            </form>
          </Box>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Go back to{" "}
            <Typography
              component={Link}
              to="/login"
              color="primary"
              fontWeight={500}
              sx={{ textDecoration: "none" }}
            >
              Login
            </Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            © {new Date().getFullYear()} FlexFlow. Crafted with{" "}
            <FavoriteIcon
              sx={{
                fontSize: 14,
                color: "error.main",
                verticalAlign: "middle",
              }}
            />{" "}
            by FlexFlow
          </Typography>
        </Box>
      </Container>
    </Box>
  )
}

export default MuiForgetPassword
